import {Fragment, useMemo} from 'react';
import {formatCurrency} from '../../../utils';

/** Aggregate an array of currency rows into totals, preserving currency order. */
function aggregate(rows, orderedCurrencies) {
    const by = {};
    for (const r of rows) {
        if (!by[r.currency]) by[r.currency] = {gross: 0, net: 0, expense: 0, saved: 0, netUAH: 0, savedUAH: 0};
        by[r.currency].gross    += r.gross;
        by[r.currency].net      += r.net;
        by[r.currency].expense  += r.expense;
        by[r.currency].saved    += r.saved;
        by[r.currency].netUAH   += r.netUAH;
        by[r.currency].savedUAH += r.savedUAH;
    }
    return orderedCurrencies.filter(c => by[c]).map(c => ({currency: c, ...by[c]}));
}

/** Compute the single combined % saved across all currency rows (UAH-converted). */
function groupPct(rows) {
    const totalNetUAH   = rows.reduce((s, r) => s + r.netUAH,   0);
    const totalSavedUAH = rows.reduce((s, r) => s + r.savedUAH, 0);
    const prc = totalNetUAH ? Math.round(totalSavedUAH / totalNetUAH * 10_000) / 100 : null;
    return {value: prc};
}

const TimelineTable = ({months, eventData, monthlyRates, currencyMode, preset, currencies}) => {
    const isAllMode = currencyMode === 'ALL';
    const showYears = preset === 'all-time';

    // Per-month grouped data: { label, year, rows: [{currency, gross, net, expense, saved, netUAH, savedUAH}] }
    const monthData = useMemo(() => {
        const active = isAllMode ? currencies : [currencyMode];
        return months
            .map((m, i) => ({
                label: m.label,
                year: Number(m.endDate.slice(0, 4)),
                rows: active
                    .map(c => {
                        const d          = eventData[c];
                        const rate       = monthlyRates?.[c]?.[i] ?? 1;
                        const gross      = d?.[i]?.income     ?? 0;
                        const net        = d?.[i]?.net        ?? 0;
                        const expense    = d?.[i]?.expense    ?? 0;
                        const transferIn = d?.[i]?.transferIn ?? 0;
                        const saved      = net - expense;
                        // Incoming transfers are saved money, not earned income, so they
                        // count toward savedUAH (numerator) but are excluded from netUAH,
                        // the % Saved denominator (real income only).
                        return {currency: c, gross, net, expense, saved, netUAH: (net - transferIn) * rate, savedUAH: saved * rate};
                    })
                    .filter(r => r.gross > 0 || r.expense > 0),
            }))
            .filter(m => m.rows.length > 0);
    }, [months, eventData, monthlyRates, currencyMode, currencies, isAllMode]);

    // Grouped by year — only computed when needed
    const yearGroups = useMemo(() => {
        if (!showYears) return [];
        const active = isAllMode ? currencies : [currencyMode];
        const map = {};
        for (const m of monthData) {
            if (!map[m.year]) map[m.year] = [];
            map[m.year].push(m);
        }
        return Object.keys(map)
            .map(Number)
            .sort((a, b) => b - a)
            .map(year => ({
                year,
                months: map[year].slice().reverse(),
                totals: aggregate(map[year].flatMap(m => m.rows), active),
            }));
    }, [monthData, showYears, currencies, currencyMode, isAllMode]);

    const totalRows = useMemo(() => {
        const active = isAllMode ? currencies : [currencyMode];
        return aggregate(monthData.flatMap(m => m.rows), active);
    }, [monthData, currencies, currencyMode, isAllMode]);

    // ── Render helpers ────────────────────────────────────────────────

    // Data cells for one currency row (no % saved — that is a spanning cell per group)
    const renderDataCells = (row, extraClass) => {
        const b  = `timeline-table__td${extraClass ? ` ${extraClass}` : ''}`;
        const pn = row.saved >= 0 ? 'timeline-table__td--pos' : 'timeline-table__td--neg';
        return (
            <>
                {isAllMode && <td className={`${b} timeline-table__td--currency`}>{row.currency}</td>}
                <td className={`${b} timeline-table__td--num`}>{formatCurrency(row.gross, row.currency)}</td>
                <td className={`${b} timeline-table__td--num`}>{formatCurrency(row.net, row.currency)}</td>
                <td className={`${b} timeline-table__td--num timeline-table__td--neg`}>{formatCurrency(row.expense, row.currency)}</td>
                <td className={`${b} timeline-table__td--num ${pn}`}>{formatCurrency(row.saved, row.currency)}</td>
            </>
        );
    };

    // % saved cell spanning all currency rows in a group
    const renderPctCell = (rows, extraClass, rowSpan) => {
        const {value} = groupPct(rows);
        const b  = `timeline-table__td timeline-table__td--num timeline-table__td--pct`;

        let ec = extraClass ? ` ${extraClass} ` : '';

        if (value != null) {
            if (value < 0) {
                ec = ' timeline-table__td--neg ' + ec;
            } else if (value < 50) {
                ec = ' timeline-table__td--mid ' + ec;
            } else {
                ec = ' timeline-table__td--pos ' + ec;
            }
        }

        return <td rowSpan={rowSpan} className={`${b}${ec}`}>{value != null ? `${value}%` : '—'}</td>;
    };

    const renderMonthGroup = ({label, rows}) => (
        <tbody key={label} className="timeline-table__month-group">
            {rows.map((row, ri) => (
                <tr key={`${label}-${row.currency}`} className="timeline-table__row">
                    {ri === 0 && (
                        <td rowSpan={rows.length} className="timeline-table__td timeline-table__td--month">{label}</td>
                    )}
                    {renderDataCells(row)}
                    {ri === 0 && renderPctCell(rows, null, rows.length)}
                </tr>
            ))}
        </tbody>
    );

    const renderYearRows = (year, totals) =>
        totals.map((row, ri) => (
            <tr key={`year-${year}-${row.currency}`} className="timeline-table__row--year">
                {ri === 0 && (
                    <td rowSpan={totals.length} className="timeline-table__td timeline-table__td--year">{year}</td>
                )}
                {renderDataCells(row, 'timeline-table__td--year')}
                {ri === 0 && renderPctCell(totals, 'timeline-table__td--year', totals.length)}
            </tr>
        ));

    const renderTotalRows = rows =>
        rows.map((row, ri) => (
            <tr key={`total-${row.currency}`}>
                {ri === 0 && (
                    <td rowSpan={rows.length} className="timeline-table__td timeline-table__td--total">Total</td>
                )}
                {renderDataCells(row, 'timeline-table__td--total')}
                {ri === 0 && renderPctCell(rows, 'timeline-table__td--total', rows.length)}
            </tr>
        ));

    // ── Render ────────────────────────────────────────────────────────

    return (
        <div className="timeline-table-wrap">
            <table className="timeline-table">
                <thead>
                    <tr>
                        <th className="timeline-table__th">Month</th>
                        {isAllMode && <th className="timeline-table__th">Currency</th>}
                        <th className="timeline-table__th timeline-table__th--num">Gross</th>
                        <th className="timeline-table__th timeline-table__th--num">Net</th>
                        <th className="timeline-table__th timeline-table__th--num">Expenses</th>
                        <th className="timeline-table__th timeline-table__th--num">Saved</th>
                        <th className="timeline-table__th timeline-table__th--num">% Saved</th>
                    </tr>
                </thead>
                {showYears
                    ? yearGroups.map(({year, months: ym, totals}) => (
                        <Fragment key={year}>
                            <tbody className="timeline-table__year-group">
                                {renderYearRows(year, totals)}
                            </tbody>
                            {ym.map(m => renderMonthGroup(m))}
                        </Fragment>
                      ))
                    : [...monthData].reverse().map(m => renderMonthGroup(m))
                }
                <tfoot>
                    {renderTotalRows(totalRows)}
                </tfoot>
            </table>
        </div>
    );
};

export default TimelineTable;